'use client'

import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { JourneySlideItem } from './journey-slide-item'

interface JourneyData {
  label: React.ReactNode
  title: string
  description: React.ReactNode
  image: string
}

interface JourneyContainerProps {
  journeyData: JourneyData[]
}

export const JourneyContainer: React.FC<JourneyContainerProps> = ({
  journeyData,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({
      delay: 5000,
    }),
  ])
  const [activeIndex, setActiveIndex] = useState(0)

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => {
      setActiveIndex(emblaApi.selectedScrollSnap())
    }
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi])

  const { label, title } = journeyData[0] // Label and title are the same for all slides
  const currentDescription = journeyData[activeIndex]?.description

  return (
    <div className='relative my-10 overflow-hidden bg-[#FEF6F4] md:my-20 md:rounded-2xl lg:mx-15'>
      <div className='flex flex-col lg:flex-row lg:items-stretch'>
        {/* Text Column (Static) */}
        <div className='flex basis-1/2 flex-col justify-center p-5 pb-6 pt-10 lg:p-16 2xl:p-22'>
          <div>
            <p className='mb-2 font-criteria text-base font-medium uppercase text-error-500 md:text-lg'>
              {label}
            </p>
            <h2
              className='font-criteria text-3xl font-bold uppercase italic leading-normal text-neutral-900 md:text-[34px] md:leading-normal 2xl:text-[44px]'
              dangerouslySetInnerHTML={{ __html: title as string }}
            />
            <div className='mt-4 hidden space-y-4 font-sans text-base leading-6 text-neutral-700 md:block md:text-lg md:leading-7'>
              {currentDescription}
            </div>
          </div>

          <div className='mt-8 hidden items-center gap-4 md:flex'>
            <button
              onClick={scrollPrev}
              className='flex h-11 w-11 items-center justify-center rounded-full bg-neutral-200 text-neutral-900 transition-colors hover:bg-neutral-300'
            >
              <ArrowLeft size={20} />
            </button>
            <button
              onClick={scrollNext}
              className='flex h-11 w-11 items-center justify-center rounded-full bg-neutral-900 text-white transition-colors hover:bg-neutral-800'
            >
              <ArrowRight size={20} />
            </button>
          </div>
        </div>

        {/* Carousel Column (Dynamic) */}
        <div className='basis-1/2 md:overflow-hidden' ref={emblaRef}>
          <div className='flex h-full'>
            {journeyData.map((slide, index) => (
              <JourneySlideItem
                key={index}
                image={slide.image}
                description={slide.description}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
